// pages/purchase-entry/StepIndicator.tsx

import { Check } from 'lucide-react';
import { useInvoiceEntryStore, InvoiceEntryStep } from '@/stores/invoiceEntryStore';
import { cn } from '@/lib/utils';

const steps: { key: InvoiceEntryStep; label: string }[] = [
  { key: 'upload', label: 'Upload' },
  { key: 'extraction', label: 'Processing' },
  { key: 'batch-preview', label: 'Review' },
];

const stepOrder: InvoiceEntryStep[] = ['upload', 'extraction', 'batch-preview'];

export function StepIndicator() {
  const { currentStep } = useInvoiceEntryStore();

  // 'duplicate' is an alternative endpoint after extraction - treat as extraction complete
  const effectiveStep = currentStep === 'duplicate' ? 'extraction' : currentStep;
  const currentIndex = stepOrder.indexOf(effectiveStep);
  const isDuplicateFlow = currentStep === 'duplicate';

  return (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center max-w-2xl">
        {steps.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          // Show amber/warning style for extraction step when duplicate detected
          const isDuplicateIndicator = isDuplicateFlow && step.key === 'extraction';

          return (
            <div key={step.key} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
                  isCompleted && !isDuplicateIndicator && 'bg-green-500 text-white',
                  isDuplicateIndicator && 'bg-amber-500 text-white',
                  isCurrent && !isDuplicateIndicator && 'bg-primary text-primary-foreground',
                  !isCompleted && !isCurrent && !isDuplicateIndicator && 'bg-muted text-muted-foreground'
                )}
              >
                {isCompleted || isDuplicateIndicator ? <Check className="h-4 w-4" /> : index + 1}
              </div>
              <span
                className={cn(
                  'text-xs mt-1 text-center',
                  isDuplicateIndicator && 'font-medium text-amber-600',
                  isCurrent && !isDuplicateIndicator && 'font-medium text-primary',
                  !isCurrent && !isDuplicateIndicator && 'text-muted-foreground'
                )}
              >
                {isDuplicateIndicator ? 'Duplicate' : step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={cn(
                  'w-24 h-0.5 mx-2',
                  index < currentIndex ? 'bg-green-500' : 'bg-muted'
                )}
              />
            )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
